import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useFirstOrderDiscount } from "@/hooks/useFirstOrderDiscount";
import { Badge } from "@/components/ui/badge";

interface CouponInputProps {
  onApply: (code: string, discount: number) => void;
  userId?: string | null;
  orderTotal?: number;
}

export const CouponInput = ({ onApply, userId, orderTotal }: CouponInputProps) => {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const { toast } = useToast();
  const { isEligible, loading: discountLoading, applyFirstOrderDiscount } = useFirstOrderDiscount(userId);

  // Auto-apply first order discount for eligible users
  useEffect(() => {
    const autoApplyFirstOrderDiscount = async () => {
      if (isEligible && !appliedCoupon && orderTotal && orderTotal >= 25) {
        const coupon = await applyFirstOrderDiscount();
        if (coupon) {
          onApply(coupon.code, coupon.discount_percentage);
          setAppliedCoupon(coupon.code);
        }
      }
    };

    if (!discountLoading) {
      autoApplyFirstOrderDiscount();
    }
  }, [isEligible, discountLoading, appliedCoupon, orderTotal, applyFirstOrderDiscount, onApply]);

  const handleApplyCoupon = async () => {
    if (!code) return;
    
    setLoading(true);
    try {
      const { data: coupons, error } = await supabase
        .from('coupons')
        .select('*')
        .eq('code', code.toUpperCase())
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;

      if (!coupons) {
        toast({
          title: "Invalid coupon",
          description: "Please check your coupon code and try again.",
          variant: "destructive",
        });
        return;
      }

      // Check if coupon is expired
      if (coupons.valid_until && new Date(coupons.valid_until) < new Date()) {
        toast({
          title: "Expired coupon",
          description: "This coupon has expired.",
          variant: "destructive",
        });
        return;
      }

      // Check minimum order amount for WELCOME15
      if (coupons.code === 'WELCOME15' && orderTotal && orderTotal < 25) {
        toast({
          title: "Minimum order required",
          description: "This discount requires a minimum order of £25.",
          variant: "destructive",
        });
        return;
      }

      onApply(code.toUpperCase(), coupons.discount_percentage);
      setAppliedCoupon(code.toUpperCase());
      toast({
        title: "Coupon applied!",
        description: `${coupons.discount_percentage}% discount has been applied to your order.`,
      });
      setCode("");
    } catch (error) {
      console.error('Error applying coupon:', error);
      toast({
        title: "Invalid coupon",
        description: "Please check your coupon code and try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* First Order Discount Banner */}
      {isEligible && !appliedCoupon && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-green-800 flex items-center gap-2">
                🎉 Welcome Discount Available!
                <Badge variant="secondary" className="bg-green-100 text-green-800">15% OFF</Badge>
              </h4>
              <p className="text-sm text-green-600">
                {orderTotal && orderTotal >= 25 
                  ? "Your 15% welcome discount will be automatically applied" 
                  : `Spend £${(25 - (orderTotal || 0)).toFixed(2)} more to qualify for 15% off your first order`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Applied Coupon Display */}
      {appliedCoupon && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-green-600 font-medium">✅ Coupon Applied:</span>
              <Badge variant="secondary" className="bg-green-100 text-green-800">{appliedCoupon}</Badge>
            </div>
          </div>
        </div>
      )}

      {/* Manual Coupon Input */}
      <div className="flex gap-2">
        <Input
          placeholder="Enter coupon code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="uppercase"
          disabled={!!appliedCoupon}
        />
        <Button 
          onClick={handleApplyCoupon}
          disabled={!code || loading || !!appliedCoupon}
          variant="secondary"
        >
          Apply
        </Button>
      </div>

      {appliedCoupon && (
        <p className="text-xs text-gray-500">
          Only one coupon can be used per order
        </p>
      )}
    </div>
  );
};